// Session manager — one session per company.
//
// There is no central login service. A token issued by one company is worthless
// with any other, so compromising one server does not open the remaining
// conversations, and its outage does not stop them working.

import { contactById, markNeedsReverify, saveDistributorName, saveSession } from '$lib/db/contacts';
import type { ContactRow } from '$lib/db/db';

import { refreshSession } from './distributor-api';
import { ClientError } from './errors';

/** One refresh per contact at a time — parallel requests after a token expires
 *  would fire several renewals at once, some invalidating the others. */
const inFlight = new Map<string, Promise<string>>();

/** Returns a valid token, renewing it when needed. */
export function sessionToken(contact: ContactRow): Promise<string> {
  const running = inFlight.get(contact.accountId);
  if (running !== undefined) {
    return running;
  }
  const work = freshToken(contact).finally(() => {
    inFlight.delete(contact.accountId);
  });
  inFlight.set(contact.accountId, work);
  return work;
}

async function freshToken(contact: ContactRow): Promise<string> {
  // The row may be stale by the time we run — re-read so a token refreshed a
  // moment ago is not refreshed again with its predecessor.
  const current = (await contactById(contact.accountId)) ?? contact;
  const token = current.token;
  if (token === null || token.length === 0) {
    throw new ClientError('unauthenticated');
  }

  // A minute of margin so the token does not expire in flight, between the check
  // and the request reaching the server.
  const valid = current.tokenExpiresMs !== null && Date.now() + 60_000 < current.tokenExpiresMs;
  if (valid) {
    return token;
  }

  try {
    const refreshed = await refreshSession({
      serverUrl: current.serverUrl,
      accountId: current.accountId,
      token,
    });
    await saveSession({
      accountId: current.accountId,
      token: refreshed.token,
      expiresAtMs: refreshed.expiresAtMs,
      threadId: refreshed.threadId.length === 0 ? (current.threadId ?? '') : refreshed.threadId,
      quarantineUntilMs: refreshed.quarantineUntilMs,
    });
    // A renamed company is picked up on the next refresh, without a directory
    // republish.
    await saveDistributorName(current.accountId, refreshed.distributorName);
    return refreshed.token;
  } catch (error) {
    if (error instanceof ClientError && error.needsLogin) {
      // The number has to be verified again with this company. The contact stays —
      // the local history must remain visible the whole time.
      await markNeedsReverify(current.accountId);
    }
    throw error;
  }
}
